import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { logger } from "@amzn/afxtaco-utilities";
import { reducerType } from "../../../redux/reducers";
import { AppDispatch } from "../../../redux/store";
import { setCognitoError, AuthState } from "../../../redux/reducers/Auth";
import { CognitoLambdaErrorType } from "@amzn/afxtaco-types";
import { AllowlistErrorDisplay } from "./AllowlistErrorDisplay";

interface TacoCognitoErrorHandlerProps {
  children: React.ReactNode;
  handleCognitoLambdaError?: (
    errorType: CognitoLambdaErrorType | null,
    errorDescription: string | null,
    defaultMessage: React.ReactElement,
  ) => React.ReactElement;
}

/**
 * Checks for Cognito Lambda errors in URL query params and displays
 * the appropriate error page. Wraps the app so errors are caught
 * before the auth flow starts.
 */
const TacoCognitoErrorHandler = (
  props: TacoCognitoErrorHandlerProps,
): React.ReactElement => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  // Parse error synchronously so children are gated on the very first render.
  // Without this, useEffect fires after render and children (AuthenticationWrapper)
  // could initiate a Cognito redirect before the error is dispatched to Redux.
  const parsedCognitoError = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return parseCognitoError(searchParams);
  }, [location.search]);

  // Persist to Redux in useEffect (avoids dispatch-during-render infinite loop).
  // Re-parse inside effect (cheap) to satisfy exhaustive-deps without object ref issues.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const parsed = parseCognitoError(searchParams);
    if (parsed) {
      dispatch(setCognitoError(parsed));
      logger.info({
        loggerMsg: "Cognito Lambda error detected",
        error: parsed.error,
        errorType: parsed.errorType,
        errorDescription: parsed.errorDescription,
      });
    }
  }, [location.search, dispatch]);

  const isSignedIn = useSelector((state: reducerType) => state.Auth.signedIn);
  const cognitoError = useSelector(
    (state: reducerType) => state.Auth.cognitoError,
  );

  // Use synchronously-parsed value OR Redux state (covers first render + subsequent)
  const effectiveCognitoError = cognitoError || parsedCognitoError;

  if (!isSignedIn && effectiveCognitoError) {
    const defaultMessage = getDefaultCognitoLambdaError(
      effectiveCognitoError.errorType,
      effectiveCognitoError.errorDescription || null,
    );
    return (
      props.handleCognitoLambdaError?.(
        effectiveCognitoError.errorType,
        effectiveCognitoError.errorDescription || null,
        defaultMessage,
      ) ?? defaultMessage
    );
  }

  return <>{props.children}</>;
};

/** Parse a CognitoLambdaErrorType out of the query parameters */
const parseCognitoError = (
  searchParams: URLSearchParams,
): AuthState["cognitoError"] | null => {
  const error = searchParams.get("error");
  if (!error || error !== "invalid_request") {
    return null;
  }

  const errorDescription = searchParams.get("error_description") || "";
  const re = /<CognitoLambdaError>(?<errorType>[A-Z_]*)<\/CognitoLambdaError>/g;
  const matches = [...errorDescription.matchAll(re)];

  if (matches.length !== 1) {
    return { error, errorType: null, errorDescription };
  }

  const errorType = matches[0].groups?.errorType;
  if (
    errorType &&
    Object.values(CognitoLambdaErrorType).includes(
      errorType as CognitoLambdaErrorType,
    )
  ) {
    return {
      error,
      errorType: errorType as CognitoLambdaErrorType,
      errorDescription,
    };
  }

  return { error, errorType: null, errorDescription };
};

/** Default error display for each CognitoLambdaErrorType */
const getDefaultCognitoLambdaError = (
  errorType: CognitoLambdaErrorType | null,
  errorDescription: string | null,
): React.ReactElement => {
  switch (errorType) {
    case CognitoLambdaErrorType.ACCESS_DENIED: {
      const tagEnd = "</CognitoLambdaError>: ";
      const tagEndIndex = errorDescription?.indexOf(tagEnd) ?? -1;
      const customMessage =
        tagEndIndex >= 0 && errorDescription
          ? errorDescription.substring(tagEndIndex + tagEnd.length)
          : "You are not in the TACO pilot program. Contact your administrator for access.";

      return <AllowlistErrorDisplay message={customMessage} />;
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="max-w-md w-full mx-4">
        <div className="bg-card border border-destructive/20 rounded-lg p-6 shadow-lg">
          <h1 className="text-lg font-semibold text-destructive mb-4">
            Unexpected Authentication Error
          </h1>
          <p className="text-sm text-muted-foreground mb-4">
            An unexpected authentication error occurred. Please try again.
          </p>
          <button
            onClick={() => {
              window.location.href = window.location.origin;
            }}
            className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  );
};

export default TacoCognitoErrorHandler;
